<template>
  <form novalidate
    @submit.prevent="submit"
    class="event-promotion-first-step-form"
  >
    <layout-section-header
      :title="$t('event_information')"
      :title-right-text="$t('step_1_of_2')"
      :has-trigger="false"
    />

    <magic-container>
      <platform-select
        v-model="platform"
        :is-multiple="false"
        :error-messages="platformErrors"
      />
      <v-autocomplete
        v-model="localEvent"
        :items="events"
        item-title="name"
        item-value="id"
        return-object
        :label="$t('local_event_name')"
        :error-messages="localEventErrors"
        @blur="v$.localEvent.$touch()"
        :loading="isLoadingLocalEvent"
        @click="() => isLocalEventFieldFocused = true"
        :disabled="platform.length === 0"
      />
    </magic-container>

    <magic-container>
      <v-autocomplete
        v-model="globalEventAsset"
        :items="assets"
        item-title="name"
        item-value="id"
        return-object
        :label="$t('select_global_event_assets')"
        :hint="$t('select_global_event_assets_hint')"
        :error-messages="globalEventAssetErrors"
        @blur="v$.globalEventAsset.$touch()"
        @click="onGlobalEventAssetFocus"
        @update:search="onEventSearch"
      />
        
      <v-autocomplete
        v-model="promotionType"
        :items="promotionsTypes"
        :label="$t('promotion_type')"
        type="text"
        item-title="name"
        item-value="id"
        :error-messages="promotionTypeErrors"
        :hide-details="false"
        persistent-hint
        :hint="$t('event_promotion_type_hint')"
        @blur="v$.promotionType.$touch()"
      />
    </magic-container>

    <div class="form-buttons mt-4">
      <v-btn
        variant="text"
        @click="cancel"
      >
        {{ $t('cancel') }}
      </v-btn>

      <v-btn
        color="primary"
        class="trigger"
        variant="flat"
        type="submit"
      >
        {{ $t('next_step') }}
      </v-btn>
    </div>
  </form>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { mapGetters } from 'vuex';

export default {
  data() {
    return {
      platform: [],
      localEvent: null,
      globalEventAsset: null,
      promotionType: null,
      isLoadingLocalEvent: false,
      isLocalEventFieldFocused: false,
    };
  },
  created() {
    // If the user is coming back from the theming or banner page,
    // we need to get the data from the store
    if (this.$route.query.step === 'back') {
      this.getStoreData();
    }

    if (this.promotionsTypes.length === 0) {
      this.$store.dispatch('events/getPromotionsTypes');
    }
  },
  watch: {
    platform(val) {
      this.getLocalEvents(val);
    },
  },
  computed: {
    ...mapGetters('events', ['promotionsTypes', 'assets']),
    ...mapGetters('pepper', ['events']),
    promotionTypeErrors() {
      return this.v$.promotionType.$errors.map(error => {
        if (error.$validator === 'required') {
          return this.$t('required');
        }
        return error.$message;
      });
    },
    platformErrors() {
      return this.v$.platform.$errors.map(error => {
        if (error.$validator === 'required') {
          return this.$t('required');
        }
        return error.$message;
      });
    },
    localEventErrors() {
      if (this.isLocalEventFieldFocused && this.platform?.length === 0) {
        return ['please select a platform'];
      }
      return this.v$.localEvent.$errors.map(error => {
        if (error.$validator === 'required') {
          return this.$t('required');
        }
        return error.$message;
      });
    },
    globalEventAssetErrors() {
      return this.v$.globalEventAsset.$errors.map(error => {
        if (error.$validator === 'required') {
          return this.$t('required');
        }
        return error.$message;
      });
    },
    activePromotion() {
      return this.$store.getters['events/activePromotion'];
    },
  },
  methods: {
    getStoreData() {
      this.platform = this.activePromotion?.country?.code;
      this.localEvent = this.activePromotion?.event;
      this.globalEventAsset = this.activePromotion?.asset;
      this.promotionType = this.activePromotion?.type;
    },
    onGlobalEventAssetFocus() {
      if (this.assets.length === 0) {
        this.$store.dispatch('events/getAssets');
      }
    },
    onEventSearch(search) {
      this.$store.dispatch('events/getAssets', {search});
    },
    getLocalEvents(country) {
      this.isLoadingLocalEvent = true;
      if (country?.length > 0) {
        this.$store.dispatch('pepper/getEvents', { countries: [country] })
          .catch(err => err)
          .finally(() => {
            this.isLoadingLocalEvent = false;
          });
      }
    },
    cancel() {
      this.clear();
      const { step, ...listQuery } = this.$route.query;
      this.$router.push({ name: 'event-promotions', query: listQuery });
    },
    async submit() {
      const isValid = await this.v$.$validate();
      if (!isValid) {
        return;
      }

      this.$store.dispatch('events/setActivePromotion', {
        country: { code: this.platform },
        event: { ...this.localEvent },
        asset: { ...this.globalEventAsset },
        type: this.promotionType,
      }).then(() => {
        if (this.promotionType === 1) {
          const { step, ...nextQuery } = this.$route.query;
          this.$router.push({ name: 'event-promotion-new-theming', query: nextQuery });
        } else if (this.promotionType === 2) {
          const { step, ...nextQuery } = this.$route.query;
          this.$router.push({ name: 'event-promotion-new-banner', query: nextQuery });
        } else {
          return this.$store.dispatch('alerts/set', {
            data: {
            message: 'Something went wrong. Try again later.',
          },
            alertType: 'error',
          });
        }
      });
    },
    clear() {
      this.v$.$reset();
      this.platform = '';
      this.localEvent = null;
      this.globalEventAsset = null;
      this.promotionType = null;
    },
  },
  setup () {
    return { v$: useVuelidate() }
  },
  validations() {
    return {
      platform: { required },
      localEvent: { required },
      globalEventAsset: { required },
      promotionType: { required },
    };
  },
};
</script>

<style>
.event-promotion-first-step-form {
  background-color: rgb(var(--v-theme-surface));
  padding: 1.5rem 1rem;
}
</style>
